"use client"

import {
  SELLER_REFERRAL_LS_CAPTURED_AT_KEY,
  SELLER_REFERRAL_LS_KEY,
  SELLER_REFERRAL_PARAM,
  getSellerReferralAttributionWindowDays,
} from "@/lib/seller-referral/constants"
import {
  buildSellerReferralCodeSetCookieValue,
  normalizeSellerReferralCode,
  readSellerReferralCodeFromDocumentCookie,
} from "@/lib/seller-referral/seller-code-cookie"
import { resolveSellerReferralCodeForPageCapture } from "@/lib/seller-referral/resolve-capture"
import {
  resolveSellerReferralCapturedAtAtSignup,
  resolveSellerReferralCodeAtSignup,
} from "@/lib/seller-referral/resolve-signup-seller"

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined"
}

function safeGetLocalStorage(): Storage | null {
  if (!isBrowser()) return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function cookieMaxAgeSeconds(): number {
  return getSellerReferralAttributionWindowDays() * 24 * 60 * 60
}

function persistSellerReferralCodeCookie(code: string): void {
  if (!isBrowser()) return
  const secure = window.location.protocol === "https:"
  document.cookie = buildSellerReferralCodeSetCookieValue(code, secure, cookieMaxAgeSeconds())
}

function readCapturedAtFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  const raw = storage.getItem(SELLER_REFERRAL_LS_CAPTURED_AT_KEY)?.trim()
  return raw || null
}

function writeCapturedAtToLocalStorage(iso: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(SELLER_REFERRAL_LS_CAPTURED_AT_KEY, iso)
}

function readFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  return normalizeSellerReferralCode(storage.getItem(SELLER_REFERRAL_LS_KEY))
}

function writeToLocalStorage(code: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(SELLER_REFERRAL_LS_KEY, code)
}

function readFromUrl(): string | null {
  if (!isBrowser()) return null
  try {
    const url = new URL(window.location.href)
    return normalizeSellerReferralCode(url.searchParams.get(SELLER_REFERRAL_PARAM))
  } catch {
    return null
  }
}

/** Último clique em `?vendedor=` antes do signup sobrescreve código e reinicia a janela. */
export function captureSellerReferralFromPage(): void {
  if (!isBrowser()) return

  const resolved = resolveSellerReferralCodeForPageCapture({
    pathname: window.location.pathname,
    urlCode: readFromUrl(),
    localStorageCode: readFromLocalStorage(),
    cookieCode: readSellerReferralCodeFromDocumentCookie(document.cookie),
  })
  if (!resolved.code) return

  writeToLocalStorage(resolved.code)
  persistSellerReferralCodeCookie(resolved.code)
  if (resolved.resetWindow) {
    writeCapturedAtToLocalStorage(new Date().toISOString())
  }
}

export function getSellerReferralCode(): string | null {
  if (!isBrowser()) return null
  return resolveSellerReferralCodeAtSignup({
    urlCode: readFromUrl(),
    localStorageCode: readFromLocalStorage(),
    cookieCode: readSellerReferralCodeFromDocumentCookie(document.cookie),
  }).code
}

export function getSellerReferralCapturedAt(): string | null {
  if (!isBrowser()) return null
  const urlCode = readFromUrl()
  return resolveSellerReferralCapturedAtAtSignup({
    hasUrlCode: Boolean(urlCode),
    localStorageCapturedAt: readCapturedAtFromLocalStorage(),
  })
}

export function initSellerReferralCapture(): void {
  captureSellerReferralFromPage()
}

export function getSellerReferralSignupPayload(): {
  code: string
  capturedAt: string | null
  source: import("@/lib/seller-referral/resolve-signup-seller").SellerReferralSignupSource
} | null {
  if (!isBrowser()) return null
  const resolved = resolveSellerReferralCodeAtSignup({
    urlCode: readFromUrl(),
    localStorageCode: readFromLocalStorage(),
    cookieCode: readSellerReferralCodeFromDocumentCookie(document.cookie),
  })
  if (!resolved.code || !resolved.source) return null
  return {
    code: resolved.code,
    capturedAt: getSellerReferralCapturedAt(),
    source: resolved.source,
  }
}

/** Indica se há código de vendedor capturado (para suprimir MGM no cadastro). */
export function hasSellerReferralCapture(): boolean {
  return Boolean(getSellerReferralCode())
}
